import React, { useMemo } from 'react';
import { Breadcrumbs, Link, Box, Paper, Chip } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import HomeIcon from '@mui/icons-material/Home';
import { BreadcrumbItem, getBreadcrumbsForPath } from './BreadcrumbConfig';

interface BreadcrumbNavigationProps {
  items?: BreadcrumbItem[];  // Optional manual items
  competitionId?: string;    // Optional competition ID
  data?: {                   // Optional data for dynamic breadcrumb labels
    competitionName?: string;
    [key: string]: any;
  };
  useAutoConfig?: boolean;   // Flag to use automatic configuration
}

const BreadcrumbNavigation: React.FC<BreadcrumbNavigationProps> = ({ 
  items, 
  competitionId, 
  data = {},
  useAutoConfig = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // If competitionId is provided, add it to data
  const breadcrumbData = useMemo(() => {
    if (competitionId && !data.competitionId) {
      return { ...data, competitionId };
    }
    return data;
  }, [competitionId, data]);
  
  // Determine which breadcrumb items to use
  const breadcrumbItems = useMemo(() => {
    if (items && !useAutoConfig) {
      // Use manually provided items
      return items;
    }
    
    // Generate breadcrumbs from current path and data
    return getBreadcrumbsForPath(location.pathname, breadcrumbData);
  }, [items, location.pathname, breadcrumbData, useAutoConfig]);
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 1.5, 
        borderRadius: 1,
        bgcolor: 'background.default',
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Breadcrumbs 
        separator={<NavigateNextIcon fontSize="small" color="action" />}
        aria-label="breadcrumb"
      >
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          
          // Current page (last item) - not clickable
          if (isLast) {
            return (
              <Chip
                key={item.label}
                label={item.label}
                size="small"
                icon={item.icon ? item.icon as React.ReactElement : undefined}
                color="primary"
                variant="filled"
              />
            );
          }
          
          // Clickable breadcrumb item
          return (
            <Link
              key={`${item.label}-${index}`}
              component="button"
              underline="hover"
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                fontSize: '0.9rem',
                color: 'text.secondary',
                '&:hover': {
                  color: 'primary.main'
                }
              }}
              color="inherit"
              onClick={() => item.path && navigate(item.path)}
            >
              {item.icon && <Box sx={{ mr: 0.5, display: 'flex' }}>{item.icon}</Box>}
              {item.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Paper>
  );
};

export default BreadcrumbNavigation;